<template>
  <dd-header-card
      v-if="createsTask"
      header="Taak instellingen"
      data-dusk="show-task-settings"
  >
    <dd-card-stacked-list>

      <dd-card-list-row>
        <dd-card-list-item title="Type">
          {{ taskTypeDescription }}
        </dd-card-list-item>
        <dd-card-list-item title="Beknopte omschrijving">
          {{ value.task_title }}
        </dd-card-list-item>
      </dd-card-list-row>
      <dd-card-list-row>
        <dd-card-list-item title="Notitie toevoegen">
          {{ value.task_description }}
        </dd-card-list-item>
        <dd-card-list-item title="Toewijzen aan gebruiker(s)">
          <user-badge-group-component v-if="hasTaskUsers"
                                      :user-ids="value.task_assigned_users"></user-badge-group-component>
          <template v-else>Geen</template>
        </dd-card-list-item>
      </dd-card-list-row>

      <dd-card-list-row>
        <dd-card-list-item title="Toewijzen aan afdeling(en)">
          <user-department-badge-group-component v-if="hasTaskUserDepartments"
                                                 :user-department-ids="value.task_assigned_user_departments"></user-department-badge-group-component>
          <template v-else>Geen</template>
        </dd-card-list-item>
        <dd-card-list-item title="Labels">
          <show-product-label-component v-for="label in productLabels" :key="label.id"
                                        :product-label="label"></show-product-label-component>
          <template v-if="!productLabels || productLabels.length === 0">
            Geen
          </template>
        </dd-card-list-item>
      </dd-card-list-row>
      <dd-card-list-row>
        <dd-card-list-item title="Datum">
          {{ taskDateDescription }}
        </dd-card-list-item>
        <dd-card-list-item title="Categorie">
          {{ taskCategoryDescription }}
        </dd-card-list-item>
      </dd-card-list-row>
      <dd-card-list-row>
        <dd-card-list-item title="Prioriteit">
          {{ taskPriorityDescription }}
        </dd-card-list-item>
      </dd-card-list-row>
    </dd-card-stacked-list>
  </dd-header-card>
</template>

<script>
import { CategoryEnum, TaskPriorityEnum, TaskTypeEnum } from "~/enums";
import UserBadgeComponent from "~/modules/user/components/BadgeComponent.vue";
import UserBadgeGroupComponent from "~/modules/user/components/BadgeGroupComponent";
import UserDepartmentBadgeGroupComponent from "~/modules/user_department/components/BadgeGroupComponent";
import EmailAccountBadgeComponent from "~/modules/email_account/components/BadgeComponent";
import productLabelStore from "~/modules/product_label/store";
import ShowProductLabelComponent from "~/modules/product_label/components/ShowProductLabelComponent.vue";
import { mapActions } from "vuex";

export default {
  name: "ShowTaskSettingsComponent",
  props: {
    value: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      productLabels: [],
    };
  },
  components: {
    UserBadgeComponent,
    EmailAccountBadgeComponent,
    UserBadgeGroupComponent,
    UserDepartmentBadgeGroupComponent,
    ShowProductLabelComponent,
  },
  created() {
    const PRODUCT_LABEL_STORE_KEY = "$_product_label";
    // eslint-disable-next-line no-underscore-dangle
    if (!(PRODUCT_LABEL_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(PRODUCT_LABEL_STORE_KEY, productLabelStore);
    }

    this.initProductLabels();
  },
  computed: {
    createsTask() {
      return this.value.create_task || false;
    },
    hasTaskUsers() {
      return this.value.task_assigned_users.length > 0;
    },
    hasTaskUserDepartments() {
      return this.value.task_assigned_user_departments.length > 0;
    },
    taskTypeDescription() {
      switch (Number(this.value.task_type)) {
        case TaskTypeEnum.GENERAL_TASK:
          return "Algemene taak";
        case TaskTypeEnum.CHECK_TASK:
          return "Controle taak";
        case TaskTypeEnum.CORRESPOND_TASK:
          return "Correspondentie taak";
        case TaskTypeEnum.CALL_BACK_REQUEST:
          return "Terugbelverzoek";
        case TaskTypeEnum.BIRTHDAY_TASK:
          return "Verjaardag";
        case TaskTypeEnum.MAINTENANCE_PRIVATE:
          return "Onderhoud particulier ";
        case TaskTypeEnum.MAINTENANCE_BUSINESS:
          return "Onderhoud zakelijk";
        default:
          return "onbekend";
      }
    },
    taskDateDescription() {
      return `Over ${this.value.task_create_in_days || 1} dagen`;
    },
    taskCategoryDescription() {
      switch (Number(this.value.task_category)) {
        case CategoryEnum.CUSTOMER:
          return "Klanten";
        case CategoryEnum.BANKING:
          return "Bankzaken";
        case CategoryEnum.INSURANCE:
          return "Polissen";
        case CategoryEnum.DAMAGE:
          return "Schades";
        case CategoryEnum.MORTGAGE:
          return "Hypotheken";
        case CategoryEnum.MAILING:
          return "Mails";
        case CategoryEnum.DOSSIER:
          return "Dossiers";
        case CategoryEnum.CREDIT:
          return "Kredieten";
        case CategoryEnum.INCOME:
          return "Inkomens";
        case CategoryEnum.RETIREMENT:
          return "Pensioenen";
        case CategoryEnum.POSSESSION:
          return "Vermogens";
        case CategoryEnum.OBLIGATION:
          return "Verplichtingen";
        case CategoryEnum.CURRENT_LIVING_CONDITION:
          return "Huidige woonsituaties";
        case CategoryEnum.TENANT:
          return "Bedrijf";
        case CategoryEnum.AGENCY:
          return "Maatschappijen";
        case CategoryEnum.TASK:
          return "Taak";
        case CategoryEnum.CUSTOMER_PORTAL:
          return "Klantportaal";
        case CategoryEnum.ASSESSMENT_PORTAL:
          return "Inventarisatiestraat";
        default:
          return "onbekend";
      }
    },
    taskPriorityDescription() {
      switch (Number(this.value.task_priority)) {
        case TaskPriorityEnum.CRITICAL:
          return "Kritiek";
        case TaskPriorityEnum.IMPORTANT:
          return "Belangrijk";
        case TaskPriorityEnum.NORMAL:
          return "Normaal";
        case TaskPriorityEnum.INSIGNIFICANT:
          return "Onbelangrijk";
        default:
          return "onbekend";
      }
    },
  },
  methods: {
    ...mapActions({
      fetchProductLabelsByIds: "$_product_label/fetchProductLabelsByIds",
    }),
    initProductLabels() {
      if (this.value.task_product_labels !== null && this.value.task_product_labels !== undefined) {
        this.productLabels = this.value.task_product_labels;
      }

      if (
          this.value.task_assigned_product_labels !== null
          && this.value.task_assigned_product_labels !== undefined
          && this.value.task_assigned_product_labels.length > 0
      ) {
        this.fetchProductLabelsByIds(this.value.task_assigned_product_labels)
            .then((productLabels) => {
              this.productLabels = productLabels;
            })
            .catch((error) => {
              this.$notification.error({
                message: "Er is een fout opgetreden bij het ophalen van de labels",
                description: error.message,
              });
            });
      }
    },
  },
};
</script>


<style scoped>
</style>
